import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, ArrowUp, ChevronDown, ChevronRight, Folder, Info, X,
} from 'lucide-react';
import { categoriesApi } from '../../api/endpoints/categories';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Alert';
import { getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { Category } from '../../types/category';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PendingChange {
  categoryId: string;
  /** null means "make root" (remove parent) */
  newParentId: string | null;
  /** What the parentId was before this local edit */
  originalParentId: string | undefined;
  type: 'add-child' | 'make-root';
}

interface HierarchyEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Accent colours for nesting depth                                   */
/* ------------------------------------------------------------------ */

const DEPTH_COLORS = [
  'border-accent/40',
  'border-emerald-500/40',
  'border-amber-500/40',
  'border-rose-500/40',
  'border-violet-500/40',
  'border-cyan-500/40',
];

function depthColor(depth: number) {
  return DEPTH_COLORS[depth % DEPTH_COLORS.length];
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function HierarchyEditor({ isOpen, onClose }: HierarchyEditorProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();

  /* ---- local state ---- */
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addChildOpen, setAddChildOpen] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /* ---- fetch categories ---- */
  const { data, isLoading } = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId && isOpen,
  });

  const allCategories: Category[] = data?.categories ?? [];

  /* ---- apply pending changes to build a "virtual" category list ---- */
  const virtualCategories = useMemo(() => {
    return allCategories.map((cat) => {
      const change = pendingChanges.get(cat._id);
      if (!change) return cat;
      return {
        ...cat,
        parentId: change.newParentId ?? undefined,
      };
    });
  }, [allCategories, pendingChanges]);

  /* ---- tree helpers on the virtual list ---- */
  const rootCategories = useMemo(
    () => virtualCategories.filter((c) => !c.parentId),
    [virtualCategories],
  );

  const getChildren = useCallback(
    (parentId: string): Category[] =>
      virtualCategories.filter((c) => c.parentId === parentId),
    [virtualCategories],
  );

  /** All descendant IDs of a category (to prevent circular nesting) */
  const getDescendantIds = useCallback(
    (parentId: string): Set<string> => {
      const ids = new Set<string>();
      const queue = [parentId];
      while (queue.length > 0) {
        const current = queue.shift()!;
        const children = virtualCategories.filter((c) => c.parentId === current);
        for (const child of children) {
          ids.add(child._id);
          queue.push(child._id);
        }
      }
      return ids;
    },
    [virtualCategories],
  );

  /** Categories eligible to become children of `parentId` */
  const getEligibleChildren = useCallback(
    (parentId: string): Category[] => {
      const descendants = getDescendantIds(parentId);
      return virtualCategories.filter(
        (c) =>
          c._id !== parentId &&       // not self
          !c.parentId &&               // currently root-level
          !descendants.has(c._id),     // not already a descendant
      );
    },
    [virtualCategories, getDescendantIds],
  );

  /* ---- actions ---- */
  const addChild = (parentId: string, childId: string) => {
    const child = allCategories.find((c) => c._id === childId);
    if (!child) return;

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(childId, {
        categoryId: childId,
        newParentId: parentId,
        originalParentId: child.parentId,
        type: 'add-child',
      });
      return next;
    });

    // auto-expand the parent
    setExpanded((prev) => new Set(prev).add(parentId));
    setAddChildOpen(null);
  };

  const makeRoot = (childId: string) => {
    const child = allCategories.find((c) => c._id === childId);
    if (!child) return;

    // If there's already a pending change that added this as a child,
    // just remove that pending change (revert to original)
    const existing = pendingChanges.get(childId);
    if (existing && existing.type === 'add-child' && !child.parentId) {
      // The original was root, so just remove the pending change
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(childId);
        return next;
      });
      return;
    }

    setPendingChanges((prev) => {
      const next = new Map(prev);
      // If there's an existing pending change that was of type add-child and the
      // original was also root, just delete the entry instead of creating make-root
      if (existing && !existing.originalParentId) {
        next.delete(childId);
      } else {
        next.set(childId, {
          categoryId: childId,
          newParentId: null,
          originalParentId: child.parentId,
          type: 'make-root',
        });
      }
      return next;
    });
  };

  const undoChange = (categoryId: string) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(categoryId);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ---- save ---- */
  const pendingCount = pendingChanges.size;

  const handleSave = async () => {
    if (pendingCount === 0) return;
    setIsSaving(true);

    try {
      const updates = Array.from(pendingChanges.values());
      // Execute all updates in parallel
      await Promise.all(
        updates.map((change) =>
          categoriesApi.update(storeId!, change.categoryId, {
            parentId: change.newParentId ?? '',
          }),
        ),
      );

      queryClient.invalidateQueries({ queryKey: ['categories', storeId] });
      toast('success', t('categories.hierarchySaveSuccess', 'Hierarchy updated successfully'));
      setPendingChanges(new Map());
      onClose();
    } catch {
      toast('error', t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setPendingChanges(new Map());
    setAddChildOpen(null);
    onClose();
  };

  /* ---- render helpers ---- */
  function renderCategoryIcon(cat: Category) {
    if (cat.iconImage) {
      return (
        <img
          src={getCdnUrl(cat.iconImage, currentStore?._id, 'categories')}
          alt=""
          className="h-5 w-5 object-contain"
        />
      );
    }
    if (cat.icon) {
      return <span className="h-5 w-5" dangerouslySetInnerHTML={{ __html: cat.icon }} />;
    }
    return <Folder className="h-5 w-5" />;
  }

  function renderNode(cat: Category, depth: number) {
    const children = getChildren(cat._id);
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(cat._id);
    const change = pendingChanges.get(cat._id);
    const isNewChild = change?.type === 'add-child';
    const isPendingRoot = change?.type === 'make-root';
    const isChildCategory = !!cat.parentId;
    const eligible = getEligibleChildren(cat._id);
    const isAddChildOpenHere = addChildOpen === cat._id;

    return (
      <div key={cat._id} className={depth > 0 ? 'ms-6' : ''}>
        {/* Node row */}
        <div
          className={`
            relative flex items-center gap-3 rounded-lg px-3 py-2 transition-all
            ${depth > 0 ? `border-s-2 ${depthColor(depth)}` : ''}
            ${isNewChild ? 'ring-2 ring-success/50 bg-success/5' : 'bg-bg-tertiary'}
            ${isPendingRoot ? 'opacity-50 line-through' : ''}
          `}
        >
          {/* Expand/collapse toggle */}
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(cat._id)}
              className="shrink-0 rounded p-0.5 text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}

          {/* Icon */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
            {renderCategoryIcon(cat)}
          </div>

          {/* Name */}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {cat.name}
          </span>

          {/* Pending badge */}
          {isNewChild && (
            <button
              onClick={() => undoChange(cat._id)}
              title={t('common.undo', 'Undo')}
              className="shrink-0 rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success hover:bg-success/25 transition-colors"
            >
              {t('categories.newChild', 'new')}
              <X className="ms-1 inline h-3 w-3" />
            </button>
          )}
          {isPendingRoot && (
            <button
              onClick={() => undoChange(cat._id)}
              title={t('common.undo', 'Undo')}
              className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning hover:bg-warning/25 transition-colors"
            >
              {t('categories.movedToRoot', 'moved to root')}
              <X className="ms-1 inline h-3 w-3" />
            </button>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Add child */}
            <div className="relative">
              <button
                onClick={() =>
                  setAddChildOpen(isAddChildOpenHere ? null : cat._id)
                }
                title={t('categories.addChild', 'Add Child')}
                className="rounded-lg p-1.5 text-success hover:bg-success/10 transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>

              {/* Dropdown for selecting child */}
              {isAddChildOpenHere && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setAddChildOpen(null)}
                  />
                  <div className="absolute end-0 top-full z-20 mt-1 w-56 rounded-lg border border-border-default bg-bg-secondary shadow-xl overflow-hidden">
                    <div className="px-3 py-2 border-b border-border-default">
                      <p className="text-xs font-medium text-text-muted">
                        {t('categories.selectChild', 'Select a category to nest')}
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {eligible.length === 0 ? (
                        <p className="px-3 py-3 text-xs text-text-muted text-center">
                          {t('categories.noEligible', 'No available categories')}
                        </p>
                      ) : (
                        eligible.map((c) => (
                          <button
                            key={c._id}
                            onClick={() => addChild(cat._id, c._id)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-start hover:bg-bg-hover transition-colors"
                          >
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent/10 text-accent">
                              {renderCategoryIcon(c)}
                            </div>
                            <span className="truncate">{c.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Make root (only for children) */}
            {isChildCategory && !isPendingRoot && (
              <button
                onClick={() => makeRoot(cat._id)}
                title={t('categories.makeRoot', 'Make Root')}
                className="rounded-lg p-1.5 text-danger hover:bg-danger/10 transition-colors"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  /* ---- main render ---- */
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('categories.manageHierarchy', 'Manage Hierarchy')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            disabled={pendingCount === 0}
          >
            {t('common.save')}
            {pendingCount > 0 && (
              <span className="ms-1.5 rounded-full bg-white/20 px-1.5 py-0.5 text-xs">
                {pendingCount}
              </span>
            )}
          </Button>
        </>
      }
    >
      {/* Instructions panel */}
      <div className="mb-4">
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="flex w-full items-center gap-2 rounded-lg bg-accent/5 border border-accent/15 px-3 py-2 text-sm text-accent transition-colors hover:bg-accent/10"
        >
          <Info className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-start font-medium">
            {t('categories.hierarchyInstructions', 'How it works')}
          </span>
          {showInstructions ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
        </button>
        {showInstructions && (
          <div className="mt-2 rounded-lg bg-accent/5 border border-accent/10 px-4 py-3 text-xs text-text-secondary space-y-1">
            <p>
              {t(
                'categories.hierarchyHelp1',
                'Use "Add Child" (+) to nest a root category under another category.',
              )}
            </p>
            <p>
              {t(
                'categories.hierarchyHelp2',
                'Use "Make Root" (arrow up) to move a child category back to the top level.',
              )}
            </p>
            <p>
              {t(
                'categories.hierarchyHelp3',
                'Changes are saved locally until you click Save. You can undo individual changes by clicking the colored badge.',
              )}
            </p>
          </div>
        )}
      </div>

      {/* Tree */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : allCategories.length === 0 ? (
        <div className="py-12 text-center text-sm text-text-muted">
          {t('categories.empty')}
        </div>
      ) : (
        <div className="space-y-1">
          {rootCategories.map((cat) => renderNode(cat, 0))}

          {/* Show pending "make root" categories that were children, now shown at root level */}
          {/* (These are already included in rootCategories via virtualCategories) */}
        </div>
      )}

      {/* Pending changes summary */}
      {pendingCount > 0 && (
        <div className="mt-4 flex items-center justify-between rounded-lg bg-warning/5 border border-warning/15 px-3 py-2">
          <span className="text-xs text-warning font-medium">
            {t('categories.pendingChanges', '{{count}} pending change(s)', {
              count: pendingCount,
            })}
          </span>
          <button
            onClick={() => setPendingChanges(new Map())}
            className="text-xs text-text-muted hover:text-danger transition-colors"
          >
            {t('categories.discardAll', 'Discard all')}
          </button>
        </div>
      )}
    </Modal>
  );
}
